import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'

import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import { useNavigate } from 'react-router-dom'
import API from '../../apis/axios'
import { apiAuthentication } from '../../apis/staff/staff'
import { User } from '../../types/user'

interface AuthContextProps {
  user: User | null
  login: (token: string) => void
  logOut: () => void
}

const AuthContext = createContext<AuthContextProps | null>(null)

export default function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem('hrme_user')
    return savedUser ? JSON.parse(savedUser) : null
  })
  const navigate = useNavigate()

  /**
   * Logs in the user using the provided token.
   * @param {string} token - The authentication token.
   */
  const login = async (token: string) => {
    const res = await apiAuthentication.loginByToken(token)

    if (res.data.data) {
      API.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const decodedToken = jwtDecode(token) as { exp: number }
      const expireTime = new Date(decodedToken.exp * 1000)
      Cookies.set('hrme_token', token, { expires: expireTime, path: '/' })
      setUser(res.data.data)
      localStorage.setItem('hrme_user', JSON.stringify(res.data.data))
      navigate('/time-sheet/check')
    }
  }

  const logOut = () => {
    setUser(null)
    localStorage.removeItem('hrme_user')
    Cookies.remove('hrme_token')
    navigate('/login')
  }

  useEffect(() => {
    if (!user || !Cookies.get('hrme_token')) {
      localStorage.removeItem('user')
      Cookies.remove('hrme_token')
      setUser(null)
    }
  }, [user, Cookies.get('hrme_token')])

  return <AuthContext.Provider value={{ user, login, logOut }}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
